import React, { useMemo, useState } from "react";
import "./RoomCard.css";
import {
  CalendarCheck,
  InfoCircle,
  ShieldFillExclamation,
  PersonFill,
  House as Bed,
  ChevronDown,
  ChevronUp,
} from "react-bootstrap-icons";
import { useSearchParams } from "next/navigation";
import { parseQueryString } from "@/utils/queryString";
import { convertToNights } from "@/utils/helper";


interface SubRoom {
  description: string;
}

interface Room {
  description: string;
  non_refundable: boolean;
  boarding_details: string;
  hotelCharges: { name: string; currency: string; amount: number }[];
  currency: string;
  priceWithMarkup: string;
  no_of_rooms: number;
  cancellation_policy?: { deadline?: string };
  rooms?: SubRoom[];
}

interface RoomType {
  [key: string]: Room[];
}

interface RoomCardProps {
  loading: boolean;
  allRooms: RoomType;
  getRoomSummary: () => string;
  getCancellationPolicyText: (room: any) => string;
  getTax: (room: Room) => {
    hotelCharges: { name: string; currency: string; amount: number }[];
  };
  toggle: (room: any) => void;
  reserveRooom: (room: any) => void;
  setIsRoomSelected: (value: boolean) => void;
}
const ForkKnifeIcon = () => (
  <svg width="5em" height="5em" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3 2.5a2.5 2.5 0 0 1 5 0v.5h1V2.5a2.5 2.5 0 0 1 5 0v.5h1V2.5a2.5 2.5 0 0 1-5 0v-.5H8v.5a2.5 2.5 0 0 1-5 0v-.5H3v.5z" />
    <path d="M3 4.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-1z" />
  </svg>
);


const RoomCard: React.FC<RoomCardProps> = ({
  loading,
  allRooms,
  getRoomSummary,
  getCancellationPolicyText,
  getTax,
  toggle,
  reserveRooom,
  setIsRoomSelected,
}) => {
  const [showAllDeals, setShowAllDeals] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [showAllRoomTypes, setShowAllRoomTypes] = useState<boolean>(false);

  // Helper function to add spaces to camelCase or snake_case names
  const addSpaces = (str: string) =>
    str
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim();

  // Toggle show/hide for deals within a room type
  const handleToggleDeals = (roomType: string) => {
    setShowAllDeals((prev) => ({
      ...prev,
      [roomType]: !prev[roomType],
    }));
  };

  // Toggle show/hide for all room types
  const handleToggleRoomTypes = () => {
    setShowAllRoomTypes((prev) => !prev);
  };

  // Find the lowest price for a room type to show "Our lowest price!" badge
  const getLowestPrice = (rooms: Room[]): number => {
    return Math.min(
      ...rooms.map((room) => parseFloat(room.priceWithMarkup) || Infinity)
    );
  };

  // Get room types to display (limit to 2 unless showAllRoomTypes is true)
  const roomTypes = showAllRoomTypes
    ? Object.entries(allRooms)
    : Object.entries(allRooms).slice(0, 2);

  const query: any = useSearchParams();

  const parsedPayload: any = useMemo(() => {
    return query ? parseQueryString(query) : null;
  }, [query]);
  const [from, to] = parsedPayload?.dates;

  return (
    <div className="mt-20">
      <div className="room-card mb-4  rounded-5 overflow-hidden">
        {/* Header */}
        <div className="room-header row g-0 bg-light p-3 d-flex sm:d-none">
          <div className="col-md-4 text-center">
            <h6 className="mb-0 fw-semibold">Room Type</h6>
          </div>
          <div className="col-md-4 text-center">
            <h6 className="mb-0 fw-semibold">Offer Details</h6>
          </div>
          <div className="col-md-4 text-end">
            <h6 className="mb-0 fw-semibold">Total Price</h6>
          </div>
        </div>

        {/* Room Content */}
        {loading ? (
          <div className="text-center p-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : roomTypes.length > 0 ? (
          roomTypes.map(([room_type, rooms], index) => {
            const lowestPrice = getLowestPrice(rooms);
            return (
              <div className="room-content-wrapper" key={room_type}>
                <div className="row g-0 border-bottom">
                  {/* Room Type Column */}
                  <div className="col-md-4 border-end p-5 bg-light-2">
                    <h5 className="fw-semibold mb-2">{room_type}</h5>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <PersonFill className="text-muted" />
                      <span className="text-dark">{getRoomSummary()}</span>
                    </div>
                  </div>

                  {/* Deals Column */}
                  <div className="col-md-8">
                    {(showAllDeals[room_type] ? rooms : rooms.slice(0, 2)).map(
                      (room: any, dealIndex: number) => (
                        <div
                          key={dealIndex}
                          className={`p-3 ${
                            dealIndex <
                            (showAllDeals[room_type] ? rooms.length : 2) - 1
                              ? "border-bottom"
                              : ""
                          }`}
                        >
                          <div className="row align-items-center">
                            <div className="col-md-8 col-12">
                              <div className="d-flex align-items-center gap-2 mb-2">
                                {/* <ForkKnifeIcon /> */}
                                {/* <Bed className="text-muted" /> */}
                                <span className="fw-medium">
                                  {room.boarding_details}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <span className="text-12">
                                  {room.rooms?.[0]?.description ||
                                    room.description}
                                </span>
                              </div>
                              <div className="d-flex align-items-left flex-column gap-2 mb-2 text-12">
                                
                                {getTax(room)?.hotelCharges?.length > 0 && (
                                <>
                                 <span className="fw-medium">
                                         Hotel Charges & Fees
                                </span>
                                  <ul className="text-muted room_card_li text-12">
                                    {getTax(room).hotelCharges.map(
                                      (charge, chargeIndex) => (
                                       <>
                                        <li key={chargeIndex}>
                                          {addSpaces(charge.name)}:{" "}
                                          <strong>
                                            {charge.currency} {charge.amount}
                                          </strong>
                                        </li>
                                       </>
                                      )
                                    )}
                                  </ul>
                                </>
                              )}

                              </div>
                              
                              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                                <div
                                  className={`d-flex align-items-center gap-1 ${
                                    room.non_refundable
                                      ? "bg-danger bg-opacity-10"
                                      : "bg-success bg-opacity-10"
                                  } px-2 py-1 rounded`}
                                >
                                  {/* {room.non_refundable ? (
                                    <ShieldFillExclamation className="text-danger" />
                                  ) : (
                                    <CalendarCheck className="text-success" />
                                  )} */}
                                  <small
                                    className={`${
                                      room?.non_refundable == false
                                        ? "text-green-2"
                                        : "text-red-2"
                                    }`}
                                  >
                                    Refundable: {room?.non_refundable == false
                                      ? getCancellationPolicyText(room)
                                      : "Non-Refundable"}
                                    <InfoCircle
                                      className="text-muted ms-1"
                                      onClick={() => toggle(room)}
                                    />
                                    {/* {JSON.stringify(room?.cancellation_policy)} */}
                                  </small>
                                </div>
                              </div>
                              
                            </div>
                            <div className="col-md-4 col-12 text-md-end mt-3 mt-md-0">
                              {parseFloat(room.priceWithMarkup) ===
                                lowestPrice && (
                                <div className="d-flex justify-content-md-end mb-2">
                                  <div className="d-flex align-items-center gap-1 bg-success bg-opacity-10 px-2 py-1 rounded">
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 20 20"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M16.3969 9.4058C16.3797 9.35132 16.3501 9.30199 16.3106 9.26205C16.2711 9.22211 16.2229 9.19275 16.1701 9.1765C16.1175 9.15965 16.0616 9.15719 16.0079 9.16937C15.9541 9.18154 15.9043 9.20792 15.8631 9.24598C15.4564 9.59451 15.0078 9.88648 14.5288 10.1146C14.5052 10.1279 14.4789 10.135 14.452 10.135C14.4252 10.135 14.3988 10.1279 14.3753 10.1146C14.3513 10.1003 14.3308 10.0802 14.3157 10.0561C14.3006 10.0319 14.2913 10.0043 14.2886 9.9756C14.1484 7.96833 13.375 6.06565 12.0886 4.5638C10.8023 3.06195 9.07518 2.04522 7.17631 1.67192C7.11411 1.66078 7.05014 1.66863 6.99213 1.69452C6.93412 1.72042 6.88454 1.76325 6.84939 1.81784C6.81548 1.87276 6.79746 1.93668 6.79746 2.00198C6.79746 2.06728 6.81548 2.1312 6.84939 2.18612C7.39197 3.1966 7.67466 4.33595 7.67004 5.49369C7.66867 6.28319 7.52613 7.06552 7.24971 7.80066C7.23541 7.82823 7.21422 7.85127 7.18838 7.86733C7.16254 7.88339 7.13301 7.89186 7.10292 7.89186C7.07283 7.89186 7.0433 7.88339 7.01746 7.86733C6.99162 7.85127 6.97043 7.82823 6.95614 7.80066C6.6622 7.30599 6.43754 6.77018 6.28895 6.20941C6.27334 6.15441 6.24516 6.10418 6.20681 6.06303C6.16846 6.02188 6.12109 5.99104 6.06878 5.97315C6.01511 5.95638 5.95825 5.95395 5.90345 5.96609C5.84864 5.97824 5.79766 6.00456 5.7552 6.04264C4.99502 6.69245 4.38329 7.51036 3.96438 8.43702C3.54547 9.36368 3.32989 10.3759 3.33329 11.4001V11.7753C3.38618 12.7543 3.63742 13.7106 4.0705 14.5813C4.50359 15.4519 5.1087 16.2173 5.84607 16.827C6.58344 17.4367 7.43637 17.877 8.34878 18.1189C9.26119 18.3608 10.2124 18.3988 11.14 18.2305C12.0675 18.0622 12.9503 17.6913 13.7304 17.1423C14.5105 16.5932 15.1702 15.8785 15.6661 15.0449C16.162 14.2114 16.4829 13.278 16.6077 12.3061C16.7325 11.3342 16.6584 10.3458 16.3902 9.4058H16.3969ZM10.0052 16.4379C9.64848 16.438 9.29534 16.3637 8.96667 16.2192C8.63801 16.0748 8.34048 15.8632 8.09167 15.5969C7.84287 15.3307 7.64785 15.0152 7.51813 14.6691C7.38841 14.323 7.32663 13.9533 7.33644 13.582C7.34788 12.5555 7.64781 11.5551 8.19917 10.7042C8.75054 9.85335 9.52918 9.18937 10.4389 8.79432C10.4726 8.78046 10.5096 8.77838 10.5445 8.78841C10.5795 8.79844 10.6103 8.82003 10.6324 8.84991C10.6546 8.88105 10.6666 8.91882 10.6666 8.95761C10.6666 8.99641 10.6546 9.03417 10.6324 9.06532C10.3485 9.47969 10.1708 9.96255 10.116 10.4683C10.0611 10.9741 10.1309 11.4861 10.3188 11.956C10.3311 11.9801 10.3482 12.0012 10.3689 12.0179C10.3896 12.0347 10.4134 12.0467 10.4389 12.0533C10.4648 12.057 10.4911 12.0552 10.5164 12.0481C10.5416 12.0409 10.5652 12.0285 10.5857 12.0116C10.8126 11.8198 10.9817 11.5641 11.0727 11.275C11.0795 11.2505 11.0914 11.2278 11.1075 11.2086C11.1236 11.1893 11.1437 11.1741 11.1661 11.1638C11.1876 11.1517 11.2117 11.1453 11.2362 11.1453C11.2606 11.1453 11.2847 11.1517 11.3062 11.1638C11.7473 11.3899 12.1133 11.7483 12.357 12.1929C12.6008 12.6375 12.7112 13.1479 12.674 13.6584C12.6812 14.0255 12.6172 14.3903 12.4856 14.7309C12.3541 15.0716 12.1578 15.381 11.9085 15.6406C11.6592 15.9003 11.3621 16.1047 11.035 16.2417C10.708 16.3787 10.3577 16.4454 10.0052 16.4379Z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                    <small className="text-success fw-medium">
                                      Our lowest price!
                                    </small>
                                  </div>
                                </div>
                              )}
                              <div className="text-md-end mb-3">
                                <h6 className="mb-0">
                                  {room.currency}{" "}
                                  {parseFloat(room.priceWithMarkup)}
                                </h6>
                                <small className="text-muted">
                                  ( {room.rooms[0]?.no_of_rooms} {room.rooms[0]?.no_of_rooms > 1 ? "rooms" : "room"} x{" "}
                                  {convertToNights(from, to)} {convertToNights(from, to) > 1 ? "nights": "night"})
                                </small>
                              </div>
                              <div className="d-flex justify-content-md-end">
                                <button
                                  type="button"
                                  className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
                                  onClick={() => {
                                    setIsRoomSelected(true);
                                    setTimeout(() => reserveRooom(room), 500);
                                  }}
                                >
                                  {`Reserve ${room.no_of_rooms} rooms`}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                    {rooms && rooms?.length > 2 && (
                      <div className="p-3 border-top">
                        <button
                          className="button onHover text-decoration-none d-flex align-items-center gap-1 mx-auto fw-400"
                          onClick={() => handleToggleDeals(room_type)}
                          aria-expanded={
                            showAllDeals[room_type] ? "true" : "false"
                          }
                        >
                          {showAllDeals[room_type]
                            ? "Show less room rates"
                            : `Show more room rates (${rooms.length - 2} more)`}
                          {showAllDeals[room_type] ? (
                            <ChevronUp />
                          ) : (
                            <ChevronDown />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-3 text-center">
            <p>No rooms available.</p>
          </div>
        )}

        {/* Show more/fewer room types button */}
        {!loading && Object.keys(allRooms).length > 2 && (
          <div className="text-center mt-3 mb-3">
            <button
              className=" button px-20 fw-400 text-14 -outline-blue-1 h-41 text-blue-1  onHover d-flex align-items-center gap-1 mx-auto fw-400"
              onClick={handleToggleRoomTypes}
              aria-expanded={showAllRoomTypes ? "true" : "false"}
            >
              {showAllRoomTypes
                ? "Show fewer room types"
                : `Show ${Object.keys(allRooms).length - 2} more room types`}
              {showAllRoomTypes ? <ChevronUp /> : <ChevronDown />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomCard;
